import { useTranslation } from "react-i18next";
import { branePlatform, type BranePlatform } from "@/lib/brane-open";
import { cn } from "@/lib/utils";
import { AppleMark } from "../AppStoreLink";
import { APP_STORE_URL, PLAY_STORE_URL } from "../constants";

/*
  The store tabs: slips of ink with a platform mark and two lines, the shape
  every phone owner already reads as "install". They are not the plate: the
  plate is the one vermilion action (sign up) and these sit beside it in ink,
  so the page still has one loud thing.
*/
export function StoreTab({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <a href={APP_STORE_URL} target="_blank" rel="noreferrer" className={cn("sh-store", className)}>
      <AppleMark />
      <span className="sh-store__label">
        <span className="sh-store__small">{t("landing.app.storeSmall")}</span>
        <span className="sh-store__name">App Store</span>
      </span>
    </a>
  );
}

/* The App Store tab's twin: the Android app is public on Google Play (owner, 2026-10-28). */
export function PlayTab({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer" className={cn("sh-store", className)}>
      <PlayMark />
      <span className="sh-store__label">
        <span className="sh-store__small">{t("landing.app.playSmall")}</span>
        <span className="sh-store__name">Google Play</span>
      </span>
    </a>
  );
}

/*
  Both stores as one slip, cut in two, for a visitor who is not holding a phone:
  either could be the phone in her pocket, and one slip keeps the hero at one
  plate and one ink thing beside it. Each half is its own link.
*/
export function StorePair({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <div role="group" aria-label={t("landing.app.stores")} className={cn("sh-store-pair", className)}>
      <a href={APP_STORE_URL} target="_blank" rel="noreferrer" className="sh-store-pair__half">
        <AppleMark />
        <span className="sh-store__name">App Store</span>
      </a>
      <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer" className="sh-store-pair__half">
        <PlayMark />
        <span className="sh-store__name">Google Play</span>
      </a>
    </div>
  );
}

/* Both tabs, the visitor's own store first. */
export function StoreTabs({ platform }: { platform: BranePlatform }) {
  return platform === "android" ? (
    <>
      <PlayTab />
      <StoreTab />
    </>
  ) : (
    <>
      <StoreTab />
      <PlayTab />
    </>
  );
}

/* The hero's one tab follows the phone in the visitor's hand; a computer gets the pair. */
export function HeroStore({ platform }: { platform: BranePlatform }) {
  if (platform === "ios") return <StoreTab />;
  if (platform === "android") return <PlayTab />;
  return <StorePair />;
}

/* The Google Play triangle, one colour (Simple Icons, CC0). Sized by the tab. */
function PlayMark() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="shrink-0">
      <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z" />
    </svg>
  );
}

/*
  Which device the visitor is on, read once from the user agent (an iPad posing
  as a Mac counts as iOS, as on the Brane page). A guess is enough to choose
  which tab leads, and a wrong guess still leaves every store reachable below.
*/
export const visitorPlatform = (): BranePlatform =>
  typeof navigator === "undefined" ? "desktop" : branePlatform(navigator.userAgent, navigator.maxTouchPoints);
